import { Checkbox, CommandBar, DatePicker, DefaultButton, Dropdown, ICommandBarItemProps, IDropdownOption, Link, MessageBar, MessageBarType, Panel, PanelType, PrimaryButton, TextField } from '@fluentui/react';
import React from 'react';
import appstyles from '../styles/App.module.scss';
import BanksPanel from '../settings/banks/BanksPanel';
import { IBank, IBankInfo, IPersonBankRelationship, IStakeholder } from '../../models';
import { FormMode } from '../../utils/Enums';
import useObject from '../../hooks/useObject';
import { ApplicationContext } from '../../contexts/ApplicationContext';
import ConfirmablePanel from '../../components/ConfirmablePanel';
import ErrorDialog from '../../components/ErrorDialog';

interface IBankRelationPanelProps
{
    selectedStakeholder: IStakeholder |undefined;
    onChanged(entity: IStakeholder): void;
    onDismiss(): void;
    selectedBankInfo?: IBankInfo;
}

export const BankRelationPanel: React.FunctionComponent<IBankRelationPanelProps> = (props: React.PropsWithChildren<IBankRelationPanelProps>) => {
    const { Services } = React.useContext(ApplicationContext);    
    const { value: entity, updateValue: updateEntity } = useObject<IStakeholder>(props.selectedStakeholder ? props.selectedStakeholder : {} as IStakeholder);
    const [banks, setBanks] = React.useState<IBank[]>([]);      
    const [selectedBank, setSelectedBank] = React.useState<IBank>();
    const [showAddBankPanel, setShowAddBankPanel] = React.useState<boolean>(false);
    const [accountNo, setAccountNo] = React.useState<string>(props.selectedBankInfo ? props.selectedBankInfo.accountNo : "");
    const [relationshipNo, setRelationshipNo] = React.useState<number | undefined>(props.selectedBankInfo ? props.selectedBankInfo.id : props.selectedStakeholder? props.selectedStakeholder?.bankInfos.length + 1 : undefined);
    const [formMode, setFormMode] = React.useState<FormMode>(props.selectedBankInfo ? FormMode.Edit : FormMode.New);
    const [errorDialogText, setErrorDialogText] = React.useState<string>('');
    const [showErrorDialog, setShowErrorDialog] = React.useState<boolean>(false);

    const openErrorDialog = (message: string) => {
        setErrorDialogText(message);
        setShowErrorDialog(true);
    };

    React.useEffect(() => {        
        fetchBanks(props.selectedBankInfo ? props.selectedBankInfo.bank.regNo : -1);     
    }, []);

    const fetchBanks = (hint: number = -1) => {
        Services?.Bank.GetAll().then((result: IBank[]) => {                          
            setBanks(result);

            if(hint !== -1) {
                const bank = result.find(x => x.regNo == hint);
                setSelectedBank(bank);
            }
        });
    }

    const CreateOrUpdateBankRelationship = async () => {
        const payload = {
            personId: entity.personID,
            relationshipNo: relationshipNo,
            bankRegNo: selectedBank?.regNo,
            bankAccountNo: accountNo,
        } as IPersonBankRelationship;

        let result : string | undefined = undefined;
        switch(formMode)
        {
            case FormMode.New:
            {
                result = await Services?.PersonBankRelationship.Create(payload);
                break;
            }
            case FormMode.Edit:
            {
                result = await Services?.PersonBankRelationship.Update(payload);
                break;
            }
        }
        
        if(result === "OK")
        {
            props.onChanged(entity);
        } else
        {
            openErrorDialog(result as string);
        }
    }

    return (<>
        <ConfirmablePanel isOpen={true} isLightDismiss={false} isBlocking={true} headerText={formMode == FormMode.New ? 'Tilføj bankrelation' : 'Opdater bankrelation'} type={PanelType.medium} onDismiss={(ev?: React.SyntheticEvent<HTMLElement> | KeyboardEvent) => { props.onDismiss(); }}  onRenderFooterContent={() => {
            return <>
                <PrimaryButton styles={{root:{marginRight:"10px"}}} onClick={() => { CreateOrUpdateBankRelationship() }} text="Gem" />
                <DefaultButton onClick={() => props.onDismiss() } text="Annuller" />                  
            </>
            }}>
            <Dropdown style={{marginBottom:"5px"}} label='Vælg bank' selectedKey={selectedBank?.regNo} options={banks.map((val, i, a) => { return { key: val.regNo, text: `${val.regNo} - ${val.name}` } as IDropdownOption })} onChange={(ev, option) => { setSelectedBank(banks.find(x => x.regNo == option?.key as number)) }} />
            <MessageBar messageBarType={MessageBarType.info}>Findes banken ikke på listen? <Link onClick={() => { setShowAddBankPanel(true); }}>Opret her</Link></MessageBar>
            {showAddBankPanel && <BanksPanel formMode={FormMode.New} selected={undefined} onDismiss={(update: boolean, hint: number) => { if(update) fetchBanks(hint); setShowAddBankPanel(false); }}  />}
            <TextField label="Kontonummer" onChange={(ev, val) => { if(val !== undefined) setAccountNo(val) }} value={accountNo} />
            <TextField label="Rel. nr" disabled={relationshipNo != undefined} value={relationshipNo + ""} onChange={(ev, val) => { if(val !== undefined) setRelationshipNo(parseInt(val)) }} type={"number"} />
        </ConfirmablePanel>
        <ErrorDialog
            isOpen={showErrorDialog}
            errorText={errorDialogText}
            onDismiss={() => setShowErrorDialog(false)}
        />
    </>);
}

export default BankRelationPanel;